import { getActiveMunicipalityId } from "@/helpers/activeMunicipality";
import { apiClient } from "@/helpers/apiClient";
import type { RouteTypes } from "@energovizor/api-client";
import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  type PayloadAction,
  type SerializedError,
} from "@reduxjs/toolkit";
import { loginUser, logoutUser, registerUser, updateUser } from "./userSlice";

export type MunicipalityList = NonNullable<RouteTypes["/municipalities"]["get"]["response"]["payload"]>;

export type MunicipalitySliceState = {
  loading: boolean;
  error: SerializedError | null;
  data: MunicipalityList | null;
  /** The initial request has settled; background refreshes preserve readiness. */
  updated: boolean;
  activeId: string | null;
};

type MunicipalityThunkState = {
  municipality: MunicipalitySliceState;
  user: {
    data?: {
      verifiedEmail?: boolean;
      verifiedByAdmin?: boolean;
      type?: string;
      accountType?: string;
    } | null;
    isLoggedIn: boolean;
  };
};

const UPDATE_MUNICIPALITIES = "municipality/update";
const REFRESH_MUNICIPALITIES = "municipality/refresh";

const getAccountType = (user: MunicipalityThunkState["user"]["data"]) =>
  user?.type ?? user?.accountType;

const isEligibleMunicipalityUser = (state: MunicipalityThunkState) => {
  const user = state.user.data;
  return (
    state.user.isLoggedIn &&
    getAccountType(user) === "client" &&
    !!user?.verifiedEmail &&
    !!user?.verifiedByAdmin
  );
};

const fetchMunicipalityList = async () => {
  const res = await apiClient.get("/municipalities");
  return (res.data.payload ?? []) as MunicipalityList;
};

const initialState: MunicipalitySliceState = {
  loading: false,
  error: null,
  data: null,
  updated: false,
  activeId: getActiveMunicipalityId(),
};

export const updateMunicipalities = createAsyncThunk(
  UPDATE_MUNICIPALITIES,
  async () => fetchMunicipalityList(),
  {
    condition: (_, thunkAPI) => {
      const state = thunkAPI.getState() as MunicipalityThunkState;
      return (
        isEligibleMunicipalityUser(state) &&
        !state.municipality.loading &&
        !state.municipality.updated
      );
    },
  }
);

export const refreshMunicipalities = createAsyncThunk(
  REFRESH_MUNICIPALITIES,
  async () => fetchMunicipalityList(),
  {
    condition: (_, thunkAPI) => {
      const state = thunkAPI.getState() as MunicipalityThunkState;
      return isEligibleMunicipalityUser(state) && !state.municipality.loading;
    },
  }
);

const resetMunicipalityState = (state: MunicipalitySliceState) => {
  state.loading = false;
  state.error = null;
  state.data = null;
  state.updated = false;
  state.activeId = null;
};

const municipalitySlice = createSlice({
  name: "municipality",
  initialState,
  reducers: {
    setActiveMunicipality(state, action: PayloadAction<string | null>) {
      state.activeId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(updateMunicipalities.pending, refreshMunicipalities.pending),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        isAnyOf(updateMunicipalities.fulfilled, refreshMunicipalities.fulfilled),
        (state, action) => {
          state.loading = false;
          state.data = action.payload as MunicipalityList;
          state.updated = true;
        }
      )
      .addMatcher(
        isAnyOf(updateMunicipalities.rejected, refreshMunicipalities.rejected),
        (state, action) => {
          state.loading = false;
          state.error = action.error;
          state.updated = true;
        }
      )
      .addMatcher(
        isAnyOf(
          loginUser.pending,
          loginUser.fulfilled,
          loginUser.rejected,
          registerUser.pending,
          registerUser.fulfilled,
          registerUser.rejected,
          logoutUser.pending,
          logoutUser.fulfilled,
          logoutUser.rejected,
          updateUser.rejected
        ),
        (state) => {
          resetMunicipalityState(state);
        }
      );
  },
});

export const { setActiveMunicipality } = municipalitySlice.actions;
export default municipalitySlice.reducer;
